import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuotaProfileAssignmentPreview, QuotaProfileView } from '../../api.js';
import { toCredits } from '../../components/AllocationPanel.js';
import { DataTable, type DataColumn } from '../../components/DataTable.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import type { LimitSubjectOption } from './CreditLimitForm.js';
import { ImpactPreviewDialog } from './ImpactPreviewDialog.js';
import {
  QuotaProfileLimitsEditor,
  draftsFromLimits,
  limitsFromDrafts,
  type LimitDraft
} from './QuotaProfileLimitsEditor.js';

interface ProfileDraft {
  /** Absent while creating. */
  id?: string;
  expectedVersion?: number;
  code: string;
  name: string;
  status: 'active' | 'inactive';
  limits: LimitDraft[];
}

const NEW_DRAFT: ProfileDraft = {
  code: '',
  name: '',
  status: 'active',
  limits: []
};

/** Rank/standing profiles: one named bundle of personal caps, applied to many
 *  members at once. Not a group and not a role — the copy says so, and the
 *  panel never uses grant vocabulary. */
export function QuotaProfilesPanel({
  http,
  models,
  onToast,
  onChanged
}: {
  http: AdminHttpClient;
  models: LimitSubjectOption[];
  onToast: (toast: ToastMessage) => void;
  /** The assignment table shows profile names, so it reloads after a change. */
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<QuotaProfileView[]>([]);
  const [unassignedMemberCount, setUnassignedMemberCount] = useState(0);
  const [draft, setDraft] = useState<ProfileDraft | undefined>();
  const [preview, setPreview] = useState<QuotaProfileAssignmentPreview | undefined>();
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);

  const fail = useCallback(
    (error: unknown) => onToast({ tone: 'error', message: describeError(error, t) }),
    [onToast, t]
  );

  const reload = useCallback(async () => {
    try {
      const result = await http.listQuotaProfiles();
      setProfiles(result.items);
      setUnassignedMemberCount(result.unassignedMemberCount);
    } catch (error) {
      fail(error);
    }
  }, [fail, http]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const limits = draft ? limitsFromDrafts(draft.limits) : undefined;
  const limitsValid = limits !== undefined;
  // Editing the caps invalidates a preview taken against the previous ones.
  const editDraft = (next: ProfileDraft) => {
    setDraft(next);
    setPreview(undefined);
    setAcknowledged(false);
  };

  const runPreview = async () => {
    if (!draft?.id || !limits) return;
    setBusy(true);
    try {
      setPreview(
        await http.previewQuotaProfileImpact({
          id: draft.id,
          limits,
          status: draft.status
        })
      );
      setAcknowledged(false);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  // A new profile has nobody on it, so there is nothing to preview and nothing
  // to gate. An EDIT reaches everyone already assigned, so it is gated.
  const previewRequired = Boolean(draft?.id);
  const blockedByAcknowledgement =
    previewRequired && (preview?.wouldBlockCount ?? 0) > 0 && !acknowledged;
  const canSave =
    Boolean(draft) &&
    limitsValid &&
    draft!.code.trim() !== '' &&
    draft!.name.trim() !== '' &&
    (!previewRequired || (preview !== undefined && !blockedByAcknowledgement));

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft || !limits || !canSave) return;
    setBusy(true);
    try {
      if (draft.id === undefined) {
        await http.createQuotaProfile({
          code: draft.code.trim(),
          name: draft.name.trim(),
          limits
        });
      } else {
        await http.updateQuotaProfile({
          id: draft.id,
          expectedVersion: draft.expectedVersion ?? 1,
          name: draft.name.trim(),
          status: draft.status,
          limits
        });
      }
      onToast({ tone: 'success', message: t('quotaProfiles.saved') });
      setDraft(undefined);
      setPreview(undefined);
      await reload();
      onChanged();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (profile: QuotaProfileView) => {
    const confirmed = window.confirm(
      t('quotaProfiles.confirmDeactivate', {
        name: profile.name,
        count: profile.assignedMemberCount
      })
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      await http.deactivateQuotaProfile(profile.id);
      onToast({ tone: 'success', message: t('quotaProfiles.deactivated') });
      await reload();
      onChanged();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const limitSummary = (profile: QuotaProfileView) =>
    profile.limits.length === 0
      ? t('quotaProfiles.noLimits')
      : profile.limits
          .map((limit) =>
            t('quotaProfiles.limitSummaryRow', {
              cycle: t(`creditLimits.cycle_${limit.cycle}`),
              model: limit.catalogModelId ?? t('creditLimits.modelAll'),
              credits: toCredits(limit.creditMillis)
            })
          )
          .join(' · ');

  const columns: DataColumn<QuotaProfileView>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('quotaProfiles.name'),
        render: (row) =>
          row.status === 'active' ? (
            row.name
          ) : (
            <span title={t('quotaProfiles.inactiveHint')}>
              {t('quotaProfiles.inactiveName', { name: row.name })}
            </span>
          ),
        sortValue: (row) => row.name
      },
      {
        key: 'code',
        header: t('quotaProfiles.code'),
        render: (row) => row.code,
        sortValue: (row) => row.code
      },
      {
        key: 'limits',
        header: t('quotaProfiles.limitsSummary'),
        render: limitSummary
      },
      {
        key: 'assigned',
        header: t('quotaProfiles.assignedMembers'),
        render: (row) => row.assignedMemberCount,
        sortValue: (row) => row.assignedMemberCount
      },
      {
        key: 'actions',
        header: t('creditLimits.actions'),
        render: (row) => (
          <span className="row-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                editDraft({
                  id: row.id,
                  expectedVersion: row.version,
                  code: row.code,
                  name: row.name,
                  status: row.status,
                  limits: draftsFromLimits(row.limits)
                })
              }
            >
              {t('creditLimits.edit')}
            </button>
            {row.status === 'active' ? (
              <button type="button" disabled={busy} onClick={() => void deactivate(row)}>
                {t('quotaProfiles.deactivate')}
              </button>
            ) : null}
          </span>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy, t]
  );

  return (
    <section className="panel quota-profiles-panel">
      <div className="quota-profiles-heading">
        <h2>{t('quotaProfiles.title')}</h2>
        <button type="button" onClick={() => editDraft({ ...NEW_DRAFT })} disabled={busy}>
          {t('quotaProfiles.add')}
        </button>
      </div>
      <p className="muted">{t('quotaProfiles.subtitle')}</p>

      <DataTable
        rows={profiles}
        columns={columns}
        empty={t('quotaProfiles.emptyState')}
        getRowKey={(row) => row.id}
        defaultSort={{ key: 'code', dir: 'asc' }}
      />
      {profiles.flatMap((profile) =>
        profile.missingCycles.map((cycle) => (
          <p className="warning" role="alert" key={`${profile.id}-${cycle}`}>
            <strong>{profile.name}</strong>:{' '}
            {t('quotaProfiles.missingCycleWarning', {
              cycle: t(`creditLimits.cycle_${cycle}`)
            })}
          </p>
        ))
      )}
      {/* Not a profile: an aggregate so "who has no profile" is visible without
          leaving the screen, together with the fact that they are still capped. */}
      <p className="muted quota-profiles-unassigned">
        {t('quotaProfiles.unassignedRow', { count: unassignedMemberCount })}
      </p>

      {draft ? (
        <form className="quota-profile-editor" onSubmit={save}>
          <h3>{draft.id ? t('quotaProfiles.editTitle') : t('quotaProfiles.addTitle')}</h3>
          <label>
            <span>{t('quotaProfiles.name')}</span>
            <input
              value={draft.name}
              onChange={(event) => editDraft({ ...draft, name: event.target.value })}
              disabled={busy}
              autoComplete="off"
            />
          </label>
          <label>
            <span>{t('quotaProfiles.code')}</span>
            <input
              value={draft.code}
              onChange={(event) => editDraft({ ...draft, code: event.target.value })}
              // The code is the join key for CSV bulk assignment, so changing it
              // after the fact would silently break saved files.
              disabled={busy || draft.id !== undefined}
              autoComplete="off"
            />
          </label>
          {draft.id ? (
            <div className="quota-profile-status">
              <span>{t('quotaProfiles.status')}</span>
              <div className="segmented-control">
                {(['active', 'inactive'] as const).map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={draft.status === status ? 'active' : ''}
                    aria-pressed={draft.status === status}
                    onClick={() => editDraft({ ...draft, status })}
                    disabled={busy}
                  >
                    {t(`quotaProfiles.status_${status}`)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <QuotaProfileLimitsEditor
            drafts={draft.limits}
            models={models}
            busy={busy}
            onChange={(next) => editDraft({ ...draft, limits: next })}
          />

          {previewRequired && !preview ? (
            <p className="muted">{t('quotaProfiles.previewRequired')}</p>
          ) : null}
          {preview ? (
            <ImpactPreviewDialog
              preview={preview}
              acknowledged={acknowledged}
              busy={busy}
              onAcknowledgeChange={setAcknowledged}
              onClose={() => setPreview(undefined)}
            />
          ) : null}

          <div className="quota-profile-actions">
            {previewRequired ? (
              <button type="button" onClick={() => void runPreview()} disabled={busy || !limitsValid}>
                {t('quotaProfiles.preview')}
              </button>
            ) : null}
            <button disabled={busy || !canSave}>{t('creditLimits.save')}</button>
            <button
              type="button"
              onClick={() => {
                setDraft(undefined);
                setPreview(undefined);
              }}
              disabled={busy}
            >
              {t('creditLimits.cancelEdit')}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
